// Wi-Fi management for a device, over MQTT.
//
//   list   → publish `getallwifi`; the device answers on `<uno>/client` in one
//            of three formats — see parseWifiList, which owns all of them.
//   add    → `[<uno>]ADDWIFIssid=<ssid>pass=<password>`
//   remove → `[<uno>]REMOVEWIFIssid=<ssid>pass=<password>`
//
// ⚠️ NO `#<userId>` ON THOSE TWO. It ends up inside the password on the ESP8266
// and the credential then matches nothing — the whole reason REMOVEWIFI never
// worked. `publishAddressed`, never `publishCommand`; the firmware quote is in
// core/devices/commands.js.
//
// ⚠️ A SUCCESSFUL ADD OR REMOVE REBOOTS THE DEVICE. So the sequence is: send,
// read the ack the device publishes, then re-list once it is back
// (WIFI_REBOOT_SETTLE_MS). This used to re-list after 1.8 s, which lands in the
// middle of that reboot — so a removal that WORKED reported "no response, the
// device may be offline", and one that silently failed reported the same thing.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, List, ListItem, ListItemText,
  CircularProgress, Alert, Divider, Tooltip, Chip,
} from '@mui/material';
import { getKnownNetworks } from '../../../core/wifi/knownNetworks';
import { registerWhenDeviceOnline } from '../../../core/wifi/registerWifi';
import LocationSelect from '../../shared/LocationSelect';
import useLocationChoices from '../../../core/hooks/useLocationChoices';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiIcon from '@mui/icons-material/Wifi';
import LockIcon from '@mui/icons-material/Lock';
import { useApp } from '../../../core/state/AppContext';
import { publishAddressed, publishCommand } from '../../../core/utils/deviceController';
import { subscribeTopic, unsubscribeTopic, addMessageListener } from '../../../core/mqtt/mqttClient';
import {
  CMD_GET_ALL_WIFI, buildAddWifiMqtt, buildRemoveWifiMqtt, parseWifiList, parseWifiAck,
  isProtectedSsid, WIFI_ACK_OK, WIFI_ACK_NOT_FOUND, WIFI_ACK_ERROR,
  WIFI_REBOOT_SETTLE_MS,
} from '../../../core/devices/commands';

const LIST_TIMEOUT_MS = 6000;

// `EMPTY` is a real answer — "I hold nothing" — and parses to the same empty
// list as a status frame that is not a Wi-Fi reply at all. Only the word counts.
const SAYS_EMPTY = (payload) => /^(\[[\w-]{1,32}\]\s*)?EMPTY$/i.test(String(payload).trim());

export default function DeviceWifiPanel({ device }) {
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const [list, setList] = useState(null); // null = not yet loaded
  const [loading, setLoading] = useState(false);
  const [ssid, setSsid] = useState('');
  const [pass, setPass] = useState('');
  const [msg, setMsg] = useState(null);
  const awaitingRef = useRef(false);
  // What an ack is expected about, so an unrelated frame is not read as one:
  // { verb, ssid }.
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const settleRef = useRef(null);

  const fetchWifi = useCallback(() => {
    setLoading(true);
    awaitingRef.current = true;
    publishCommand(uno, CMD_GET_ALL_WIFI, userId);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!awaitingRef.current) return;
      awaitingRef.current = false;
      setLoading(false);
      setList([]);
      setMsg({ type: 'warning', text: 'No response — the device may be offline.' });
    }, LIST_TIMEOUT_MS);
  }, [uno, userId]);

  // Re-list once the device is back from the reboot a change causes. Also the
  // backstop for firmware that acks nothing at all, so a quiet device cannot
  // leave a stale list on screen for ever.
  const recheckAfterReboot = useCallback(() => {
    clearTimeout(settleRef.current);
    settleRef.current = setTimeout(fetchWifi, WIFI_REBOOT_SETTLE_MS);
  }, [fetchWifi]);

  useEffect(() => {
    const topic = `${uno}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;

      // Acks first. A list reply can never look like one, but "REMOVEWIFI OK
      // ssid=Home" WOULD be read as a network named that if the order were
      // reversed.
      const ack = parseWifiAck(payload);
      if (ack && pendingRef.current && ack.verb === pendingRef.current.verb) {
        const name = ack.ssid || pendingRef.current.ssid || '';
        pendingRef.current = null;
        if (ack.result === WIFI_ACK_NOT_FOUND) {
          clearTimeout(settleRef.current);
          setMsg({ type: 'error', text: `The device does not hold "${name}" — nothing was removed.` });
          return;
        }
        if (ack.result === WIFI_ACK_ERROR) {
          clearTimeout(settleRef.current);
          setMsg({ type: 'error', text: ack.text });
          return;
        }
        // OK (ESP32), or ACCEPTED (ESP8266, which acks BEFORE it acts — receipt,
        // not proof). Either way the device is about to restart; the re-list is
        // what actually settles it.
        setMsg(ack.result === WIFI_ACK_OK
          ? {
            type: 'success',
            text: `${ack.verb === 'ADDWIFI' ? 'Saved' : 'Removed'} "${name}". `
              + 'The device is restarting — re-reading its list.',
          }
          : {
            type: 'info',
            text: 'The device accepted it and is restarting. Re-reading its list —'
              + ' that is what says whether it took.',
          });
        recheckAfterReboot();
        return;
      }

      if (!awaitingRef.current) return;
      const nets = parseWifiList(payload);
      if (!nets.length && !SAYS_EMPTY(payload)) return;
      awaitingRef.current = false;
      clearTimeout(timerRef.current);
      setList(nets);
      setLoading(false);
    });
    fetchWifi();
    return () => {
      off();
      unsubscribeTopic(topic);
      clearTimeout(timerRef.current);
      clearTimeout(settleRef.current);
    };
  }, [uno, fetchWifi, recheckAfterReboot]);

  /*
   * ── The location's saved networks, offered before the typed fields ──────
   *
   * The mobile picker has had this from the start; this tab made you type. The
   * list is server data (plus a live `getallwifi` round to the site's online
   * devices), so it needs no radio and works on a desk. Click one and both
   * fields fill — the password is the point, it is the part that has to be
   * right first time.
   *
   * Loaded when the tab opens, not on mount of the dialog: the dialog has two
   * other tabs and this one is not always visited.
   */
  //
  // ⚠️ WHOSE list is a CHOICE, defaulting to the device's own location. The
  // bench case: the device is at the office on the default network and is
  // being prepared for a customer — their networks are on THEIR location, so
  // the operator switches the selector and the list (and where the network is
  // registered) follows. See core/hooks/useLocationChoices.
  const deviceLocationId = device.LocationID ?? device.LocationId ?? null;
  const [chosenLocationId, setChosenLocationId] = useState(deviceLocationId);
  useEffect(() => { setChosenLocationId(deviceLocationId); }, [deviceLocationId, uno]);
  // ⚠️ The list carries passwords: only a location the user OWNS (or any, for
  // a Super Admin) is listed or registered to — the device's own location is
  // only the default, and a guest with room access does not own it.
  const { isAllowed, loading: choicesLoading } = useLocationChoices();
  const locationId = isAllowed(chosenLocationId) ? chosenLocationId : null;
  const [known, setKnown] = useState(null); // null = not asked yet
  const [knownFailed, setKnownFailed] = useState(false);
  useEffect(() => {
    if (choicesLoading) { setKnown(null); return undefined; }
    if (!locationId) { setKnown([]); return undefined; }
    let alive = true;
    setKnown(null); setKnownFailed(false);
    getKnownNetworks(locationId).then((r) => {
      if (!alive) return;
      setKnown(r.networks || []);
      setKnownFailed(!!r.serverFailed && !(r.networks || []).length);
    }).catch(() => { if (alive) { setKnown([]); setKnownFailed(true); } });
    return () => { alive = false; };
  }, [locationId, choicesLoading]);

  // Already on the device? Then offering it again is noise.
  const onDevice = new Set((list || []).map((n) => String(n.ssid)));
  const offerable = (known || []).filter((n) => n.pass && !onDevice.has(String(n.ssid)));

  const addWifi = () => {
    const name = ssid.trim();
    if (!name) return;
    pendingRef.current = { verb: 'ADDWIFI', ssid: name };
    publishAddressed(uno, buildAddWifiMqtt(name, pass));
    // Onto the location's list once the device is heard again — the same
    // watcher every other add-Wi-Fi path uses. See core/wifi/registerWifi, and
    // the note in DeviceAppBar on why this is weaker proof for a device that
    // already has a working network.
    if (locationId) registerWhenDeviceOnline(uno, locationId, name, pass);
    setMsg({ type: 'info', text: `Sent "${name}" — waiting for the device.` });
    setSsid('');
    setPass('');
    recheckAfterReboot();
  };

  const removeWifi = (n) => {
    // The same refusal the mobile dialog makes. On the ESP32 fleet removing the
    // last stored network cannot stick anyway: an empty list is reseeded with
    // exactly these two on the next boot.
    if (isProtectedSsid(n.ssid)) {
      setMsg({
        type: 'warning',
        text: `"${n.ssid}" is a fallback network — removing it could leave the device`
          + ' with no way back online, and ESP32 firmware restores it at the next boot anyway.',
      });
      return;
    }
    pendingRef.current = { verb: 'REMOVEWIFI', ssid: n.ssid };
    publishAddressed(uno, buildRemoveWifiMqtt(n.ssid, n.pass));
    setMsg({ type: 'info', text: `Removing "${n.ssid}" — waiting for the device.` });
    recheckAfterReboot();
  };

  const refresh = () => { setMsg(null); fetchWifi(); };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }}>Saved Wi‑Fi networks</Typography>
        <Tooltip title="Refresh">
          <span><IconButton size="small" onClick={refresh} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span>
        </Tooltip>
      </Box>

      {msg && <Alert severity={msg.type} sx={{ mb: 1 }}>{msg.text}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
      ) : list && list.length ? (
        <List dense disablePadding>
          {list.map((n) => (
            <ListItem
              key={n.ssid} disableGutters
              secondaryAction={isProtectedSsid(n.ssid) ? (
                <Tooltip title="Fallback network — cannot be removed">
                  <LockIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </Tooltip>
              ) : (
                <IconButton edge="end" size="small" onClick={() => removeWifi(n)}><DeleteIcon fontSize="small" /></IconButton>
              )}
            >
              <WifiIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <ListItemText primary={n.ssid} />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No saved networks.</Typography>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Add a network</Typography>

      {/* Saved for this location — click to fill both fields. Only networks
          with a password are offered; one without saves no typing. */}
      <LocationSelect value={locationId} onChange={setChosenLocationId} sx={{ mb: 1, maxWidth: 420 }} />
      {locationId && (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {known === null ? 'Looking up networks saved for that location…'
              : offerable.length ? 'Saved there — click to fill in:'
                : knownFailed ? 'That location’s saved networks could not be loaded.'
                  : 'No other networks saved for that location.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {offerable.map((n) => (
              <Chip
                key={n.ssid}
                size="small"
                icon={<WifiIcon sx={{ fontSize: 14 }} />}
                label={n.ssid}
                variant={ssid === n.ssid ? 'filled' : 'outlined'}
                color={ssid === n.ssid ? 'primary' : 'default'}
                onClick={() => { setSsid(n.ssid); setPass(n.pass); }}
                title={n.from === 'device' ? 'Reported live by a device at this location' : 'Saved on the location'}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="SSID" value={ssid} onChange={(e) => setSsid(e.target.value)} sx={{ flex: 1, minWidth: 130 }} />
        <TextField size="small" label="Password" value={pass} onChange={(e) => setPass(e.target.value)} sx={{ flex: 1, minWidth: 130 }} />
        <Button variant="contained" onClick={addWifi} disabled={!ssid.trim()}>Add</Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        The device restarts after a change, so its list is re-read a few seconds later.
      </Typography>
    </Box>
  );
}
